/**
 * WebSocket client with auto-reconnection
 */

import WebSocket from 'ws';
import { EventEmitter } from './EventEmitter';
import { ConnectionManager } from './ConnectionManager';
import { FilterManager } from './FilterManager';
import { MetricsCollector } from './utils/metrics';
import { calculateDelay, shouldRetry } from './utils/retry';
import type {
  ClientConfig,
  FilterOptions,
  WebSocketMessage,
  ConnectionState,
  Metrics,
  WebSocketClientEvents,
} from './types';
import { ConnectionError } from './types';

const DEFAULT_WS_URL = 'wss://api.ethpending.com/ws';
const DEFAULT_HEARTBEAT_INTERVAL = 30000;
const DEFAULT_CONNECTION_TIMEOUT = 10000;

/**
 * WebSocket client for real-time pending transaction streaming
 */
export class WebSocketClient extends EventEmitter<WebSocketClientEvents> {
  private config: Required<ClientConfig>;
  private ws?: WebSocket;
  private connectionManager: ConnectionManager;
  private filterManager: FilterManager;
  private metrics: MetricsCollector;
  private reconnectAttempts = 0;
  private reconnectTimer?: NodeJS.Timeout;
  private heartbeatTimer?: NodeJS.Timeout;
  private connectionTimer?: NodeJS.Timeout;
  private pendingSubscription = false;
  private isManualDisconnect = false;

  constructor(apiKey: string, config?: Partial<ClientConfig>) {
    super();

    this.config = {
      apiKey,
      wsUrl: config?.wsUrl || DEFAULT_WS_URL,
      httpUrl: config?.httpUrl || '',
      autoReconnect: config?.autoReconnect ?? true,
      maxReconnectAttempts: config?.maxReconnectAttempts ?? Infinity,
      reconnectDelay: config?.reconnectDelay ?? 1000,
      maxReconnectDelay: config?.maxReconnectDelay ?? 60000,
      heartbeatInterval: config?.heartbeatInterval ?? DEFAULT_HEARTBEAT_INTERVAL,
      connectionTimeout: config?.connectionTimeout ?? DEFAULT_CONNECTION_TIMEOUT,
    };

    this.connectionManager = new ConnectionManager();
    this.filterManager = new FilterManager();
    this.metrics = new MetricsCollector();

    // Forward state change events
    this.connectionManager.on('stateChange', ({ oldState, newState }) => {
      this.metrics.setConnectionState(newState);
      this.emit('stateChange', { oldState, newState });
    });
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (this.connectionManager.isConnected() || this.connectionManager.isConnecting()) {
      return;
    }

    this.isManualDisconnect = false;
    this.connectionManager.connecting();

    return new Promise((resolve, reject) => {
      try {
        const url = `${this.config.wsUrl}?apiKey=${this.config.apiKey}`;
        this.ws = new WebSocket(url);

        // Set connection timeout
        this.connectionTimer = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            reject(new ConnectionError('Connection timeout'));
          }
        }, this.config.connectionTimeout);

        this.ws.on('open', () => {
          this.handleOpen();
          resolve();
        });

        this.ws.on('message', (data: WebSocket.Data) => {
          this.handleMessage(data);
        });

        this.ws.on('error', (error: Error) => {
          this.handleError(error);
          reject(error);
        });

        this.ws.on('close', (code: number, reason: Buffer) => {
          this.handleClose(code, reason.toString());
        });
      } catch (error) {
        this.connectionManager.disconnected();
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.isManualDisconnect = true;
    this.cleanup();

    if (this.ws) {
      this.connectionManager.disconnecting();
      this.ws.close(1000, 'Client disconnect');
    } else {
      this.connectionManager.disconnected();
    }
  }

  /**
   * Set transaction filters
   */
  setFilters(filters: FilterOptions): void {
    this.filterManager.setFilters(filters);

    if (this.connectionManager.isConnected() && this.ws) {
      this.sendFilterSubscription();
    } else {
      this.pendingSubscription = true;
    }
  }

  /**
   * Get current metrics
   */
  getMetrics(): Metrics {
    return this.metrics.getMetrics();
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionManager.getState();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionManager.isConnected();
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    this.clearConnectionTimer();
    this.connectionManager.connected();
    this.metrics.recordConnectionStart();
    this.reconnectAttempts = 0;

    this.startHeartbeat();

    // Send pending filter subscription if any
    if (this.pendingSubscription || this.filterManager.hasFilters()) {
      this.sendFilterSubscription();
      this.pendingSubscription = false;
    }

    this.emit('connected', undefined as void);
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(data: WebSocket.Data): void {
    try {
      const message = JSON.parse(data.toString()) as WebSocketMessage;
      this.metrics.recordMessageReceived();

      switch (message.type) {
        case 'connected':
          // Initial connection message
          break;

        case 'subscribed':
          this.emit('subscribed', message.filters);
          break;

        case 'pending_tx':
          this.emit('transaction', message.data);
          break;

        case 'error':
          const error = new Error(message.message);
          this.metrics.recordError('ApiError');
          this.emit('error', error);
          break;

        default:
          // Unknown message type
          break;
      }
    } catch (error) {
      this.metrics.recordError('ParseError');
      const err = error instanceof Error ? error : new Error(String(error));
      this.emit('error', err);
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(error: Error): void {
    this.metrics.recordError('ConnectionError');
    this.emit('error', error);
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(code: number, reason: string): void {
    this.cleanup();
    this.metrics.recordConnectionEnd();
    this.connectionManager.disconnected();
    this.emit('disconnected', { code, reason });

    // Attempt reconnection if not manually disconnected
    if (!this.isManualDisconnect && this.config.autoReconnect) {
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (!shouldRetry(this.reconnectAttempts, {
      maxAttempts: this.config.maxReconnectAttempts,
      initialDelay: this.config.reconnectDelay,
      maxDelay: this.config.maxReconnectDelay,
    })) {
      this.emit('error', new ConnectionError('Max reconnection attempts reached'));
      return;
    }

    const delay = calculateDelay(this.reconnectAttempts, {
      initialDelay: this.config.reconnectDelay,
      maxDelay: this.config.maxReconnectDelay,
    });

    this.connectionManager.reconnecting();
    this.metrics.recordReconnectAttempt();
    this.reconnectAttempts++;

    this.emit('reconnecting', this.reconnectAttempts);

    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(error => {
        this.emit('error', error);
        this.scheduleReconnect();
      });
    }, delay);
  }

  /**
   * Send filter subscription message
   */
  private sendFilterSubscription(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const filters = this.filterManager.getFilters();
    const message = {
      action: 'subscribe',
      filters,
    };

    try {
      this.ws.send(JSON.stringify(message));
      this.metrics.recordMessageSent();
    } catch (error) {
      this.metrics.recordError('SendError');
      const err = error instanceof Error ? error : new Error(String(error));
      this.emit('error', err);
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.ping();
        } catch (error) {
          // Ping failed, connection might be dead
          this.emit('error', new ConnectionError('Heartbeat failed'));
        }
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  /**
   * Clear connection timeout timer
   */
  private clearConnectionTimer(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = undefined;
    }
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.stopHeartbeat();
    this.clearConnectionTimer();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.removeAllListeners();
      this.ws = undefined;
    }
  }
}

